/**
 * RijkeTubePipeline — see FRAG_SRC header.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface RijkeTubePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;


void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  // Background — a warm dark.
  vec3 bg = mix(vec3(0.10, 0.05, 0.04), vec3(0.02, 0.01, 0.01), smoothstep(0.0, 0.7, r));
  vec3 col = bg;
  // A vertical tube in the center, with hot gas rising inside.
  // The tube walls: a vertical rectangle.
  float tubeWidth = 0.10;
  float tubeTop = 0.50;
  float tubeBot = -0.50;
  float tubeLeft = -tubeWidth * 0.5;
  float tubeRight = tubeWidth * 0.5;
  float wallDist = max(abs(p.x - (tubeLeft + tubeRight) * 0.5) - tubeWidth * 0.5, 0.0);
  // Inside the tube.
  if (p.x > tubeLeft && p.x < tubeRight && p.y > tubeBot && p.y < tubeTop) {
    // The gas density oscillates as a standing wave.
    float y = (p.y - tubeBot) / (tubeTop - tubeBot);
    // Standing wave: sin(n * pi * y) for mode n.
    float mode = 1.0 + floor(mid * 4.0);
    float wave = sin(mode * 3.14159 * y + uTime * 2.0);
    // The wave amplitude is highest at the source (bottom).
    float amp = (1.0 - y) * (0.5 + 0.5 * bass);
    // Hot gas: red at the source, fading to yellow upward.
    vec3 gasCol = mix(vec3(0.95, 0.30, 0.10), vec3(0.95, 0.85, 0.45), y);
    col = mix(col, gasCol, (0.5 + 0.5 * wave) * amp);
    // The gas is brighter near the walls.
    float wallGlow = wallDist / tubeWidth;
    col += vec3(0.95, 0.6, 0.3) * wallGlow * 0.3 * amp;
  }
  // Tube walls.
  float wallMask = 1.0 - smoothstep(0.0, 0.005, abs(p.x - tubeLeft));
  wallMask = max(wallMask, 1.0 - smoothstep(0.0, 0.005, abs(p.x - tubeRight)));
  wallMask *= step(tubeBot, p.y) * step(p.y, tubeTop);
  col = mix(col, vec3(0.6, 0.4, 0.3), wallMask * 0.7);
  // Heat source at the bottom.
  float heatD = abs(p.y - tubeBot);
  float heatMask = exp(-heatD * 20.0) * step(abs(p.x), tubeWidth);
  col += vec3(1.0, 0.5, 0.2) * heatMask * (0.5 + 0.5 * bass);
  // Pressure wave nodes — bright spots where the standing wave has zero.
  for (int n = 1; n < 5; n++) {
    float fn = float(n);
    float nodeY = tubeBot + (tubeTop - tubeBot) * fn / (1.0 + floor(mid * 4.0));
    float ndist = abs(p.y - nodeY);
    float nodeMask = exp(-ndist * 40.0) * step(abs(p.x), tubeWidth);
    col += vec3(0.95, 0.7, 0.5) * nodeMask * 0.3;
  }
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeRijkeTubeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeRijkeTubeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createRijkeTubePipeline(gl: WebGL2RenderingContext): RijkeTubePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawRijkeTube(
  gl: WebGL2RenderingContext,
  pipe: RijkeTubePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
